/**
 * Google Places (New) autocomplete for the Free Quote form's suburb field.
 *
 * Drop-in replacement for `free-quote-address-autocomplete.js`, rewritten for the
 * **Places API (New)** data API — AutocompleteSuggestion + Place.fetchFields —
 * because Google blocked the legacy `google.maps.places.Autocomplete` widget for
 * new projects on 1 March 2025. Exposes the SAME loader callback name
 * (`initFreeQuoteAddressAutocomplete`), so only the <script src> in
 * `pages/quote/free_quote.html` changes; the Maps loader line stays as-is.
 *
 * Keeps the existing styled `#city` input and renders its own suggestion
 * dropdown (the New API's data methods don't ship a widget). Behaviour matches
 * the legacy version:
 *   - AU-only suggestions (localities / suburbs / postcodes).
 *   - On pick, auto-fills #city (suburb), #zip_code (postcode), #state (NSW/VIC/…).
 *   - Suggestions are biased to the currently selected #state.
 *
 * Requires "Places API (New)" enabled on the Google Cloud project.
 */

(function () {
    'use strict';

    // Soft location bias per AU state/territory. {north,south,east,west}.
    var AU_STATE_BOUNDS = {
        NSW: { south: -37.51, west: 140.99, north: -28.16, east: 153.64 },
        VIC: { south: -39.16, west: 140.96, north: -33.98, east: 149.98 },
        QLD: { south: -29.18, west: 137.99, north: -10.68, east: 153.55 },
        WA:  { south: -35.13, west: 112.92, north: -13.69, east: 129.00 },
        SA:  { south: -38.06, west: 129.00, north: -25.99, east: 141.00 },
        TAS: { south: -43.65, west: 143.82, north: -39.57, east: 148.48 },
        NT:  { south: -26.00, west: 129.00, north: -10.97, east: 138.00 },
        ACT: { south: -35.92, west: 148.76, north: -35.12, east: 149.40 },
    };

    // New API address components carry `types` + `longText`/`shortText`.
    var COMPONENT_TYPES = {
        suburb: ['locality', 'postal_town', 'sublocality_level_1', 'sublocality'],
        state: ['administrative_area_level_1'],
        postalCode: ['postal_code'],
    };

    var DEBOUNCE_MS = 220;

    function findComponent(components, types) {
        if (!Array.isArray(components)) return null;
        for (var i = 0; i < types.length; i++) {
            for (var j = 0; j < components.length; j++) {
                var c = components[j];
                if (c && Array.isArray(c.types) && c.types.indexOf(types[i]) !== -1) {
                    return c;
                }
            }
        }
        return null;
    }

    function setSelectValue(select, value) {
        if (!select || !value) return;
        for (var i = 0; i < select.options.length; i++) {
            if (select.options[i].value === value) {
                if (select.value !== value) {
                    select.value = value;
                    select.dispatchEvent(new Event('change', { bubbles: true }));
                }
                return;
            }
        }
    }

    function setInputValue(input, value) {
        if (!input || value == null) return;
        if (input.value !== value) {
            input.value = value;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    /** Wrap #city in a relative container and build the suggestions dropdown. */
    function buildDropdown(cityInput) {
        var wrap = document.createElement('div');
        wrap.className = 'relative';
        cityInput.parentNode.insertBefore(wrap, cityInput);
        wrap.appendChild(cityInput);

        var list = document.createElement('div');
        list.id = 'fq-places-dropdown';
        list.setAttribute('role', 'listbox');
        list.className = 'hidden absolute left-0 right-0 top-full mt-1 z-30 max-h-60 ' +
            'overflow-y-auto bg-white border border-gray-200 rounded-2xl shadow-xl';
        wrap.appendChild(list);

        cityInput.setAttribute('role', 'combobox');
        cityInput.setAttribute('aria-autocomplete', 'list');
        cityInput.setAttribute('aria-expanded', 'false');
        cityInput.setAttribute('aria-controls', list.id);
        return list;
    }

    function init(places) {
        var cityInput = document.getElementById('city');
        var stateSelect = document.getElementById('state');
        var zipInput = document.getElementById('zip_code');
        if (!cityInput || !places || !places.AutocompleteSuggestion) return;

        var AutocompleteSuggestion = places.AutocompleteSuggestion;
        var AutocompleteSessionToken = places.AutocompleteSessionToken;

        var list = buildDropdown(cityInput);
        var token = new AutocompleteSessionToken();
        var suggestions = [];     // current placePrediction objects
        var activeIndex = -1;     // keyboard-highlighted row
        var debounceId = null;
        var lastQuery = '';

        function stateBounds() {
            return stateSelect ? AU_STATE_BOUNDS[stateSelect.value] || null : null;
        }

        function closeList() {
            list.classList.add('hidden');
            list.innerHTML = '';
            suggestions = [];
            activeIndex = -1;
            cityInput.setAttribute('aria-expanded', 'false');
        }

        function highlight(idx) {
            activeIndex = idx;
            Array.prototype.forEach.call(list.children, function (row, i) {
                row.classList.toggle('bg-[#ecfce8]', i === idx);
                row.setAttribute('aria-selected', i === idx ? 'true' : 'false');
            });
        }

        function renderRows() {
            list.innerHTML = '';
            if (!suggestions.length) { closeList(); return; }
            suggestions.forEach(function (pred, i) {
                var row = document.createElement('div');
                row.setAttribute('role', 'option');
                row.className = 'px-3 py-2.5 text-sm cursor-pointer hover:bg-[#ecfce8] ' +
                    (i < suggestions.length - 1 ? 'border-b border-gray-100' : '');
                row.textContent = pred.text && pred.text.text ? pred.text.text : '';
                row.addEventListener('mousedown', function (e) {
                    // mousedown (not click) so it fires before the input blur.
                    e.preventDefault();
                    choose(i);
                });
                list.appendChild(row);
            });
            list.classList.remove('hidden');
            cityInput.setAttribute('aria-expanded', 'true');
            highlight(-1);
        }

        function fetchSuggestions(input) {
            var request = {
                input: input,
                sessionToken: token,
                includedRegionCodes: ['au'],
                includedPrimaryTypes: ['locality', 'sublocality', 'postal_code'],
                language: 'en-AU',
                region: 'au',
            };
            var bounds = stateBounds();
            if (bounds) request.locationBias = bounds;

            AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
                .then(function (res) {
                    var items = (res && res.suggestions) || [];
                    suggestions = items
                        .map(function (s) { return s.placePrediction; })
                        .filter(Boolean);
                    renderRows();
                })
                .catch(function () { closeList(); });
        }

        function choose(idx) {
            var pred = suggestions[idx];
            if (!pred) return;
            var place = pred.toPlace();
            place.fetchFields({ fields: ['addressComponents', 'displayName', 'formattedAddress'] })
                .then(function () {
                    var comps = place.addressComponents || [];
                    var suburbComp = findComponent(comps, COMPONENT_TYPES.suburb);
                    var stateComp = findComponent(comps, COMPONENT_TYPES.state);
                    var postalComp = findComponent(comps, COMPONENT_TYPES.postalCode);

                    var suburbName = (suburbComp && suburbComp.longText)
                        || (place.displayName)
                        || (pred.text && pred.text.text)
                        || cityInput.value;
                    setInputValue(cityInput, suburbName);

                    if (stateComp && stateComp.shortText) {
                        setSelectValue(stateSelect, stateComp.shortText);
                    }
                    if (postalComp && postalComp.longText) {
                        setInputValue(zipInput, postalComp.longText);
                    }
                })
                .catch(function () { /* keep whatever the user typed */ })
                .then(function () {
                    // A place-details fetch ends the billing session — start a new one.
                    token = new AutocompleteSessionToken();
                    closeList();
                });
        }

        // --- input handling (debounced) ---
        cityInput.addEventListener('input', function () {
            var q = cityInput.value.trim();
            lastQuery = q;
            if (debounceId) clearTimeout(debounceId);
            if (q.length < 2) { closeList(); return; }
            debounceId = setTimeout(function () {
                if (cityInput.value.trim() === lastQuery) fetchSuggestions(lastQuery);
            }, DEBOUNCE_MS);
        });

        cityInput.addEventListener('keydown', function (e) {
            if (list.classList.contains('hidden') || !suggestions.length) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                highlight((activeIndex + 1) % suggestions.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                highlight((activeIndex - 1 + suggestions.length) % suggestions.length);
            } else if (e.key === 'Enter') {
                if (activeIndex >= 0) { e.preventDefault(); choose(activeIndex); }
            } else if (e.key === 'Escape') {
                closeList();
            }
        });

        // Close on outside click / blur.
        document.addEventListener('click', function (e) {
            if (!list.contains(e.target) && e.target !== cityInput) closeList();
        });
        cityInput.addEventListener('blur', function () {
            // Delay so a suggestion mousedown can register first.
            setTimeout(closeList, 150);
        });
    }

    // Loader callback (same name as the legacy file). Loads the Places (New)
    // library, then wires the field.
    window.initFreeQuoteAddressAutocomplete = function () {
        function boot() {
            if (!window.google || !google.maps || !google.maps.importLibrary) return;
            google.maps.importLibrary('places')
                .then(function (places) { init(places); })
                .catch(function () { /* Places unavailable — leave the plain input. */ });
        }
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', boot);
        } else {
            boot();
        }
    };
})();
